import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Heart } from "@phosphor-icons/react";
import { postsApi } from "@/lib/apiClient";
import { PassportLogo } from "@/components/Reveal";
import { useTranslation } from "react-i18next";

export default function PublicPost() {
    const { t } = useTranslation();
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [err, setErr] = useState(null);
    const [idx, setIdx] = useState(0);
    useEffect(() => { postsApi.publicPost(slug).then(setPost).catch(() => setErr(t("publicPost.postNotFoundOrPrivate"))); }, [slug, t]);

    if (err) {
        return (
            <div className="min-h-screen grain grid place-items-center text-center px-6">
                <div>
                    <div className="overline mb-3">{t("publicPost.notFoundOverline")}</div>
                    <div className="font-serif text-4xl text-white mb-3">{t("publicPost.notAvailable")}</div>
                    <div className="text-white/60 mb-6">{err}</div>
                    <Link to="/" className="btn-gold text-sm !py-2.5 !px-5">{t("publicPost.explorePassport")}</Link>
                </div>
            </div>
        );
    }
    if (!post) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;
    const media = post.media && post.media.length ? post.media : (post.image_url ? [{ url: post.image_url, kind: "image" }] : []);
    const item = media[idx];
    return (
        <div className="min-h-screen grain" data-testid="public-post">
            <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <PassportLogo size={32} />
                        <div className="hidden sm:block">
                            <div className="font-serif text-lg text-white">{t("conciergeApp.passport")}</div>
                            <div className="overline" style={{ fontSize: "8px" }}>{t("publicPost.sharedPost")}</div>
                        </div>
                    </Link>
                    <Link to="/auth" className="btn-gold text-sm !py-2 !px-4">{t("publicPost.joinPassport")}</Link>
                </div>
            </header>
            <div className="max-w-[900px] mx-auto px-6 md:px-10 py-10">
                <motion.article initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl overflow-hidden" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div className="p-5 flex items-center gap-3">
                        <img src={post.user_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${post.user_handle}`} alt="" className="w-11 h-11 rounded-full object-cover" style={{ border: "1px solid rgba(212,175,55,0.4)" }} />
                        <div>
                            <div className="text-white font-medium">{post.user_name}</div>
                            <div className="text-xs" style={{ color: "var(--gold)" }}>@{post.user_handle}</div>
                        </div>
                    </div>
                    {item && (
                        <div className="relative bg-black">
                            {item.kind === "video" ? (
                                <video src={item.url} className="w-full block" controls playsInline preload="metadata" />
                            ) : (
                                <img src={item.url} alt="" className="w-full block object-cover" />
                            )}
                            {media.length > 1 && (
                                <div className="absolute inset-x-0 bottom-3 flex justify-center gap-2">
                                    {media.map((_, i) => (
                                        <button key={i} onClick={() => setIdx(i)} className="w-2 h-2 rounded-full" style={{ background: i === idx ? "var(--gold)" : "rgba(255,255,255,0.4)" }} aria-label={`Slide ${i + 1}`} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="p-6 space-y-3">
                        {post.caption && <div className="text-white/85 leading-relaxed whitespace-pre-wrap">{post.caption}</div>}
                        {(post.location || post.country) && (
                            <div className="text-sm text-white/60 flex items-center gap-1.5">
                                <MapPin size={12} weight="fill" color="#D4AF37" />
                                {[post.location, post.country].filter(Boolean).join(", ")}
                            </div>
                        )}
                        <div className="text-xs text-white/50 flex items-center gap-2">
                            <Heart size={12} weight="fill" color="#ff5f5f" />
                            {t("publicPost.reactions", { count: post.reactions_total || 0 })} · {t("publicPost.comments", { count: post.comments || 0 })}
                        </div>
                    </div>
                </motion.article>
                <div className="text-center py-8">
                    <Link to="/auth" className="btn-gold text-sm !py-2.5 !px-6">{t("publicPost.joinToReact")}</Link>
                </div>
            </div>
        </div>
    );
}
